import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sprout, Check, Printer, Clock, Lock } from "lucide-react";

const T = { green: "#B3E5A1", greenDeep: "#7FB069", slate: "#44546A", grey: "#F2F2F2", greyMid: "#E7E6E6" };
let PS_ACCENT = { band: T.green, bandText: T.slate };
const DOC_ACCENTS = {
  "Student Support Plan": { band: "#F4B6AE", bandText: "#7A2417", title: "#B23A2E", border: "#E0897B" },
  "Support Review Record": { band: "#F6E08A", bandText: "#6E5410", title: "#A9791A", border: "#E6CB52" },
};

export default function ParentSign() {
  const { token } = useParams();
  const [data, setData] = useState(undefined); // undefined=loading, null=error
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [consent, setConsent] = useState(false);
  const [signing, setSigning] = useState(false);
  const [code, setCode] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  const load = () => {
    fetch(`${API}/public/parent-shares/${token}`)
      .then((r) => {
        if (r.status === 410) { setData("expired"); return null; }
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then((d) => { if (d) setData(d); })
      .catch(() => setData(null));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token]);

  const unlock = async (e) => {
    e.preventDefault();
    setUnlocking(true);
    try {
      const res = await fetch(`${API}/public/parent-shares/${token}/unlock`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(formatApiError(body.detail));
      setData(body);
    } catch (err) {
      toast.error(err.message || "Could not open the document.");
    } finally {
      setUnlocking(false);
    }
  };

  const sign = async (e) => {
    e.preventDefault();
    setSigning(true);
    try {
      const res = await fetch(`${API}/public/parent-shares/${token}/sign`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parent_name: name, consent, comment, code }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(formatApiError(body.detail));
      setData(body);
      toast.success("Thank you — your signature has been saved.");
    } catch (err) {
      toast.error(err.message || "Could not submit. Please try again.");
    } finally {
      setSigning(false);
    }
  };

  if (data === undefined)
    return <div className="min-h-screen flex justify-center items-center text-slate-300 bg-[#F9F9F7]"><Loader2 className="animate-spin" /></div>;

  if (data === "expired")
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-[#F9F9F7] px-6 text-center" data-testid="parent-expired">
        <Clock size={32} className="text-[#D98A6A] mb-3" />
        <h1 className="font-['Outfit'] text-2xl font-bold text-slate-800">This link has expired</h1>
        <p className="text-slate-500 mt-2">Please ask the school for a new link to view and sign this document.</p>
      </div>
    );

  if (data === null)
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-[#F9F9F7] px-6 text-center">
        <Sprout size={32} className="text-[#7FB069] mb-3" />
        <h1 className="font-['Outfit'] text-2xl font-bold text-slate-800">Link unavailable</h1>
        <p className="text-slate-500 mt-2">This sharing link is no longer active. Please ask the school for a new one.</p>
      </div>
    );

  if (data.locked)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7] px-4">
        <form onSubmit={unlock} data-testid="parent-unlock-form" className="w-full max-w-sm bg-white rounded-2xl shadow-[0_4px_24px_rgba(42,59,71,0.06)] p-7 text-center">
          <span className="inline-flex h-12 w-12 rounded-2xl items-center justify-center text-white mb-3" style={{ backgroundColor: "#7FB069" }}><Lock size={22} /></span>
          <h1 className="font-['Outfit'] text-2xl font-bold text-slate-800">{data.doc_title}</h1>
          <p className="text-slate-500 mt-2 text-sm">
            This document for <b>{data.student_first_name || "your child"}</b> is protected.
            Enter the access code the school gave you to view and sign it.
          </p>
          <input data-testid="parent-code-input" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric"
            placeholder="••••" maxLength={8}
            className="mt-5 w-full text-center tracking-[0.5em] text-2xl font-bold bg-[#F9F9F7] border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#7FB069]" />
          <button data-testid="parent-unlock-btn" type="submit" disabled={unlocking || !code.trim()}
            className="mt-4 w-full text-white rounded-full py-3 font-semibold font-['Outfit'] flex items-center justify-center gap-2 disabled:opacity-50" style={{ backgroundColor: "#7FB069" }}>
            {unlocking ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Open document
          </button>
        </form>
      </div>
    );

  const { snapshot, doc_title, status, signature } = data;
  const signed = status === "signed";
  const school = snapshot.school || {};
  const logoSrc = school.logo || "/school-logo.png";
  const schoolName = school.name || "St. Francis Xavier Junior School";
  const accent = DOC_ACCENTS[doc_title] || { band: T.green, bandText: T.slate, title: T.slate, border: T.green };
  PS_ACCENT = { band: accent.band, bandText: accent.bandText };

  return (
    <div className="min-h-screen bg-[#F9F9F7] py-8 px-4">
      <div className="max-w-3xl mx-auto flex items-center justify-end mb-4 no-print">
        {signed && (
          <button data-testid="parent-print-btn" onClick={() => window.print()}
            className="inline-flex items-center gap-2 text-white rounded-full px-5 py-2 font-semibold font-['Outfit'] transition" style={{ backgroundColor: T.slate }}>
            <Printer size={16} /> Print signed copy
          </button>
        )}
      </div>

      <div className="print-area max-w-3xl mx-auto bg-white rounded-2xl shadow-[0_4px_24px_rgba(42,59,71,0.06)] overflow-hidden">
        {/* Header */}
        <div className="px-8 sm:px-12 pt-9 pb-6 flex items-center gap-4" style={{ borderBottom: `3px solid ${accent.border}` }}>
          <img src={logoSrc} alt={schoolName} className="h-20 w-20 object-contain shrink-0" />
          <div className="min-w-0">
            <h2 className="font-['Outfit'] font-bold text-xl leading-tight" style={{ color: T.slate }}>{schoolName}</h2>
            <p className="text-sm text-slate-400">Shared with parents / guardians</p>
          </div>
        </div>

        <div className="px-8 sm:px-12 py-9 text-[15px] leading-relaxed text-slate-700">
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: accent.title }} data-testid="parent-doc-title">{doc_title}</h1>

          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-5 mt-6 rounded-xl p-5" style={{ backgroundColor: T.grey }}>
            <Detail label="Name" value={snapshot.student_name} />
            <Detail label="Class / Level" value={snapshot.class_line} />
            {(snapshot.meta || []).map((m) => <Detail key={m.label} label={m.label} value={m.value} />)}
          </div>

          {(snapshot.blocks || []).map((b) => (
            <div key={b.label}>
              <Band>{b.label}</Band>
              {b.type === "text" ? (
                <p className="mt-3 whitespace-pre-wrap">{b.value || "—"}</p>
              ) : (
                <div className="space-y-4 mt-4">
                  {(b.items || []).length === 0 && <p className="text-slate-400">—</p>}
                  {(b.items || []).map((it, j) => (
                    <div key={it.heading || j} className="rounded-xl border border-slate-200 p-5">
                      <p className="text-base font-bold font-['Outfit'] mb-2.5 pb-2.5 border-b border-slate-100" style={{ color: T.slate }}>{it.heading}</p>
                      <div className="space-y-3">
                        {it.rows.map((r, k) => (
                          <div key={r.label || k} className="sm:grid sm:grid-cols-[160px_1fr] sm:gap-4">
                            <p className="text-xs uppercase tracking-wider text-slate-400 font-bold pt-0.5">{r.label}</p>
                            <p className="whitespace-pre-wrap">{r.value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Sign-off */}
          <div className="mt-9 pt-6" style={{ borderTop: `2px solid ${T.greyMid}` }}>
            {signed ? (
              <div data-testid="parent-signed-block" className="rounded-xl p-5" style={{ backgroundColor: "#EAF6E4", border: `1px solid ${T.greenDeep}` }}>
                <p className="flex items-center gap-2 font-bold font-['Outfit']" style={{ color: T.greenDeep }}>
                  <Check size={18} /> Signed digitally
                </p>
                <p className="text-sm text-slate-700 mt-2">Signed by <b>{signature?.parent_name}</b></p>
                <p className="text-xs text-slate-500">{signature?.signed_at && new Date(signature.signed_at).toLocaleString("en-IE", { dateStyle: "long", timeStyle: "short" })}</p>
                {signature?.comment && (
                  <div className="mt-3">
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">Parent / Guardian comment</p>
                    <p className="text-sm text-slate-700 italic mt-1 whitespace-pre-wrap">{signature.comment}</p>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={sign} className="space-y-4 no-print">
                <h2 className="font-['Outfit'] text-lg font-bold" style={{ color: T.slate }}>Parent / Guardian sign-off</h2>
                <p data-testid="parent-privacy-notice" className="text-xs text-slate-500 bg-[#F9F9F7] border border-slate-100 rounded-xl px-3.5 py-2.5 leading-relaxed">
                  <b>Privacy notice:</b> {schoolName} processes this information to support your child's learning, under the school's data-protection policy and the GDPR. Your name, comment and the date/time you sign are recorded with this document. Contact the school's Data Protection Officer for access, correction or any questions.
                </p>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Your comment (optional)</label>
                  <textarea data-testid="parent-comment-input" rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
                    placeholder="Add any comments or questions for the school…"
                    className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-700 outline-none focus:border-[#7FB069] focus:bg-white resize-none transition" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Full name</label>
                  <input data-testid="parent-name-input" required value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Mary O'Brien"
                    className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-700 outline-none focus:border-[#7FB069] focus:bg-white transition" />
                </div>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input data-testid="parent-consent-checkbox" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-[#7FB069]" />
                  <span className="text-sm text-slate-600">I confirm I have read and discussed this {doc_title.toLowerCase()} with the school.</span>
                </label>
                <button data-testid="parent-sign-btn" disabled={signing || !consent || !name.trim()}
                  className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 font-semibold font-['Outfit'] text-white transition disabled:opacity-50"
                  style={{ backgroundColor: T.greenDeep }}>
                  {signing ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Sign &amp; submit
                </button>
              </form>
            )}
          </div>

          <p className="text-xs text-slate-400 mt-10 pt-6 text-center" style={{ borderTop: `1px solid ${T.grey}` }}>
            {schoolName} · {doc_title} · Aligned to the Irish primary curriculum &amp; NEPS Continuum of Support<br />
            <span className="text-slate-300">Powered by BloomTrack</span>
          </p>
        </div>
      </div>
    </div>
  );
}

function Band({ children }) {
  return (
    <h2 className="font-['Outfit'] text-lg font-bold mt-9 px-4 py-2.5 rounded-lg" style={{ backgroundColor: PS_ACCENT.band, color: PS_ACCENT.bandText }}>
      {children}
    </h2>
  );
}
function Detail({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">{label}</p>
      <p className="text-base font-semibold font-['Outfit'] mt-0.5" style={{ color: T.slate }}>{value || "—"}</p>
    </div>
  );
}
